import is from '@sindresorhus/is';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CHAT_AUTO_CLOSE_AFTER_COMPLETE_MS } from '../../constants';
import { useChatStore } from '../../stores/chat-open-store';

export type ChatAutoClose = {
    /** Closes the dock now instead of at the end of the countdown. */
    readonly accept: () => void;
    readonly cancel: () => void;
    /** Starts the countdown that closes the dock. */
    readonly schedule: () => void;
    /** Identifies the running countdown, so its progress restarts with every new one; null while none runs. */
    readonly scheduleId: number | null;
};

/** The countdown that closes the dock once an agent action resolved its request. */
export const useChatAutoClose = (): ChatAutoClose => {
    const [scheduleId, setScheduleId] = useState<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const nextScheduleIdRef = useRef(0);

    const cancel = useCallback((): void => {
        if (!is.null(timerRef.current)) clearTimeout(timerRef.current);
        timerRef.current = null;
        setScheduleId(null);
    }, []);

    const schedule = useCallback((): void => {
        cancel();
        nextScheduleIdRef.current += 1;
        setScheduleId(nextScheduleIdRef.current);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setScheduleId(null);
            useChatStore.getState().close();
        }, CHAT_AUTO_CLOSE_AFTER_COMPLETE_MS);
    }, [cancel]);

    const accept = useCallback((): void => {
        cancel();
        useChatStore.getState().close();
    }, [cancel]);

    useEffect(
        () => () => {
            if (!is.null(timerRef.current)) clearTimeout(timerRef.current);
        },
        [],
    );

    return { accept, cancel, schedule, scheduleId };
};
